import { LangStore } from "../../../store";
import type { ReferenceCacheStore } from "../../../store/ReferenceCacheStore";

export interface CustomValidator<T> {
  name: string;
  // `cache` da acceso a referencias cacheables (`.cacheable`) para validaciones
  // cross-entidad (p.ej. leer personType/taxId de una editorial referenciada).
  // `original` es el SNAPSHOT normalizado de los valores ORIGINALES (pre-edición)
  // que arma el update store (computeSnapshot), para reglas de "campo bloqueado tras
  // un evento" (comparar original vs actual, p.ej. congelar identidad si ya se validó
  // en ANSES). OJO con la forma: los escalares van con su nombre, pero las refs van
  // como `{campo}: id` (NO como `show{Campo}`) y las multi-refs como `id[]`. Por eso
  // se tipa Record<string, unknown> y no Partial<T>: acceder `original.show{Ref}?.id`
  // no compila, que es el error correcto.
  // Ambos opcionales: los validators que no los usan mantienen la firma corta.
  // Además de `errors`, un validator puede devolver `warnings` (avisos): se
  // muestran junto al campo y NUNCA impiden guardar. Los avisos se calculan en
  // un computed de MobX sobre una copia superficial de los valores: un validator
  // no debe mutar valores anidados (arrays u objetos dentro de `values`).
  validate(
    values: T,
    langStore: LangStore,
    cache?: ReferenceCacheStore,
    original?: Record<string, unknown>
  ): CustomValidationResult;
}

export type CustomValidationResult = {
  errors: Record<string, { message: string; fieldLabel: string }>;
  cardErrors?: Record<string, string[]>;
  // Opcional: los validators que devuelven solo {errors, cardErrors} siguen compilando.
  warnings?: Record<string, { message: string; fieldLabel: string }>;
};

export function runCustomValidators<T>(
  validators: CustomValidator<T>[],
  values: T,
  langStore: LangStore,
  cache?: ReferenceCacheStore,
  original?: Record<string, unknown>
): Required<CustomValidationResult> {
  const result: Required<CustomValidationResult> = {
    errors: {},
    cardErrors: {},
    warnings: {},
  };

  for (const validator of validators) {
    const validationResult = validator.validate(
      values,
      langStore,
      cache,
      original
    );

    Object.assign(result.errors, validationResult.errors);

    if (validationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        validationResult.cardErrors
      )) {
        if (!result.cardErrors[cardId]) {
          result.cardErrors[cardId] = [];
        }
        result.cardErrors[cardId].push(...messages);
      }
    }

    if (validationResult.warnings) {
      Object.assign(result.warnings, validationResult.warnings);
    }
  }

  return result;
}

// Avisos de los validators custom: no bloquean. Computed de MobX, en vivo:
// validateData corre recién al guardar y un guardado exitoso cierra el
// formulario, así que un aviso calculado ahí nunca se vería. No entran en
// `errors` ni en el gate de save. Corre sobre una copia de los valores: un
// validator que normaliza `values` no escribe estado desde un computed. La
// copia es superficial: un validator no debe mutar valores anidados (arrays u
// objetos dentro de `values`); eso sí escribiría el estado del store desde
// el computed, y MobX solo lo avisa en desarrollo si ese valor está observado.
//
// Cada validator corre en su propio try/catch: uno que falla no aporta avisos,
// pero los demás sí. Fuera de producción se loguea, para que un validator roto
// no esconda todos los avisos sin dejar rastro; en producción no se loguea.
export function collectCustomWarnings<T>(
  validators: CustomValidator<T>[],
  values: T,
  langStore: LangStore,
  cache: ReferenceCacheStore | undefined,
  original: Record<string, unknown> | undefined,
  storeLabel: string
): Record<string, { message: string; fieldLabel: string }> {
  const warnings: Record<string, { message: string; fieldLabel: string }> = {};

  for (const validator of validators) {
    try {
      const validationResult = validator.validate(
        values,
        langStore,
        cache,
        original
      );

      if (validationResult.warnings) {
        Object.assign(warnings, validationResult.warnings);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error(
          `[${storeLabel}] a custom validator failed while computing warnings:`,
          error
        );
      }
    }
  }

  return warnings;
}
